import { faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Icons } from '../assets/icons'

type Variant = { label: string; rate: number; fill?: string }
type Family = {
    Icon: (props: IconProps) => JSX.Element
    name: string
    variants: Variant[]
}

/**
 * Rates come from `NFT.stake_rate`, a Python property on the model (br1/nft/models/nft.py).
 * They are not served to the client, so this table is a mirror — if the property changes, change
 * it here too.
 *
 * Grouped by family, and sorted fastest-first inside each. The flat list this replaces ran
 * Legendary Ape → Ape → Legendary Droid → Droid → Loot Box → Common Weapon → Legendary Weapon →
 * Ultra Weapon, reflowing into two to four columns depending on width. Nothing about that order
 * survived the reflow, so the two questions people actually bring here — "what does *my* item
 * earn" and "what earns most" — both needed a scan of all eight rows.
 */
const FAMILIES: Family[] = [
    {
        Icon: Icons.Ape,
        name: 'Apes',
        variants: [
            { label: 'Legendary', rate: 120, fill: 'url(#svg_legendarygradient)' },
            { label: 'Standard', rate: 60 },
        ],
    },
    {
        Icon: Icons.Droid,
        name: 'Droids',
        variants: [
            { label: 'Legendary', rate: 90, fill: 'url(#svg_legendarygradient)' },
            { label: 'Standard', rate: 30 },
        ],
    },
    {
        Icon: Icons.Weapon,
        name: 'Weapons',
        variants: [
            { label: 'Ultra', rate: 105, fill: 'url(#svg_legendarygradient)' },
            { label: 'Legendary', rate: 45, fill: 'url(#svg_weapon_legendary_gradient)' },
            { label: 'Common', rate: 10 },
        ],
    },
    {
        Icon: Icons.Lootbox,
        name: 'Loot Boxes',
        variants: [{ label: 'Standard', rate: 50 }],
    },
]

/** The best rate on the board, used to scale the bars. */
const TOP_RATE = Math.max(...FAMILIES.flatMap((family) => family.variants.map((v) => v.rate)))

/**
 * Replaces the old "HOW DOES RENTING WORK?" panel — a dropdown you operated one option at a time
 * to learn eight numbers. Stays closed until asked.
 */
export const RateReference = () => {
    return (
        <details className='group border border-white/20 rounded'>
            <summary className='flex flex-row items-center gap-3 p-4 cursor-pointer select-none hover:bg-white/5 font-black uppercase text-sm'>
                <FontAwesomeIcon icon={faChevronRight} className='text-xs transition-transform group-open:rotate-90' />
                <span className='flex-1'>How activation works</span>
            </summary>

            <div className='border-t border-white/20 p-4 flex flex-col gap-4'>
                {/* Capped for readability — full-bleed prose on a wide monitor is a very long line. */}
                <p className='text-white/60 text-sm max-w-3xl'>
                    Activate an item and it earns BR1 Points for as long as it stays active. Points accrue
                    continuously, so there is nothing to claim, and you can deactivate at any time.
                </p>

                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4'>
                    {FAMILIES.map(({ Icon, name, variants }) => (
                        <div key={name} className='flex flex-col gap-2 rounded border border-white/10 bg-white/[0.03] p-3'>
                            <div className='flex flex-row items-center gap-2 border-b border-white/10 pb-2'>
                                <Icon className='h-6 w-6 shrink-0' />
                                <span className='text-xs font-black uppercase tracking-wider'>{name}</span>
                            </div>

                            {variants.map(({ label, rate, fill }) => (
                                <div key={label} className='flex flex-col gap-1'>
                                    <div className='flex flex-row items-baseline justify-between gap-2'>
                                        <span className='text-2xs font-bold uppercase tracking-wider text-white/50'>
                                            {label}
                                        </span>
                                        {/* The unit is said once per row instead of "PTS / DAY" eight
                                            times — repeating it was most of the visual noise. */}
                                        <span className='whitespace-nowrap text-sm font-black tabular-nums'>
                                            {rate}
                                            <span className='pl-1 text-2xs font-bold text-white/40'>/day</span>
                                        </span>
                                    </div>
                                    {/* A bar makes the ranking readable without doing arithmetic:
                                        an Ultra Weapon is visibly ten times a Common one. */}
                                    <div className='h-1 w-full bg-white/[0.07]'>
                                        <div
                                            className='h-full'
                                            style={{
                                                width: `${(rate / TOP_RATE) * 100}%`,
                                                background: fill
                                                    ? 'linear-gradient(94.38deg, #03CEFF -17.38%, #C91FFF 108.85%)'
                                                    : 'rgba(255,255,255,0.35)',
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                <p className='text-2xs uppercase tracking-wider text-white/35'>
                    Points per day, per activated item. Bars are relative to the best rate ({TOP_RATE}/day).
                </p>
            </div>
        </details>
    )
}
